<script setup lang="ts">
  import { useServices } from '@/composables/useServices';
  import type { ServicesSectionContent } from '@/models/service';
  import type { PropType } from 'vue';
  import { computed } from 'vue';
  import ServicesSectionGrid from './services/ServicesSectionGrid.vue';
  import ServicesSectionSwiper from './services/ServicesSectionSwiper.vue';

  const props = defineProps({
    servicesSectionContent: {
      type: Object as PropType<ServicesSectionContent>,
      required: true
    },
    isMobile: {
      type: Boolean,
      required: true
    },
    localePath: {
      type: Function as PropType<(to: { name: string; params: { slug: string } }) => string>,
      required: true
    }
  });

  const emits = defineEmits(['contactClick']);
  const onContactClick = () => {
    emits('contactClick');
  };

  const { wrapperRef, isModalOpen, openModal, closeModal } = useServices();

  const shouldShowMoreBtn = computed(() => props.servicesSectionContent.useCasesCount > 4);
</script>

<template>
  <section class="services-section">
    <div class="container">
      <div
        ref="wrapperRef"
        class="services-section__wrapper">
        <div class="services-section__heading">
          <h1 v-html="servicesSectionContent.heading" />
          <p>{{ servicesSectionContent.description }}</p>
        </div>
        <ServicesSectionSwiper
          v-if="isMobile"
          :services-section-content="servicesSectionContent"
          :locale-path="localePath"
          @contact-click="onContactClick" />
        <ServicesSectionGrid
          v-else
          :services-section-content="servicesSectionContent"
          :locale-path="localePath"
          @contact-click="onContactClick" />
        <div
          v-if="shouldShowMoreBtn"
          class="services-section__actions">
          <button
            class="services-section__btn-more"
            @click.prevent.stop="openModal">
            {{ $t('caseStudy.btnMore') }}
          </button>
        </div>
        <div
          v-if="servicesSectionContent.partners.length"
          class="services-section__partners">
          <span>{{ servicesSectionContent.techPartnersTitle }}:</span>
          <a
            v-for="(partner, index) in servicesSectionContent.partners"
            :key="index">
            <img
              :src="partner.logo.src"
              :alt="partner.title" />
          </a>
        </div>
      </div>
    </div>
    <transition
      name="fade"
      mode="out-in">
      <ServicesModal
        v-if="isModalOpen"
        :locale-path="localePath"
        @close="closeModal" />
    </transition>
  </section>
</template>
